import { useMemo, useState } from 'react';

import { MonthCalendar } from '@backrs_ui/common-ui/src/molecules/MonthCalendar/MonthCalendar';
import { monthOf, toISODate } from '@backrs_ui/common-ui/src/utils/dateTime.util';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useAppDispatch } from '@backrs_ui/grow-ui/src/redux/hooks';
import { setBoosterSelectedDay } from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.slice';
import { BoosterPlan } from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.types';
import { openCustomTab } from '@backrs_ui/grow-ui/src/redux/tabs/tabs.slice';

import { Box, SxProps, Theme } from '@mui/material';

import { ReplanControl } from './ReplanControl';
import { planDayToCalendarDay } from '../utils/planCalendar';

const STRINGS = appStrings.modules.satActBooster;

type StudyCalendarSectionProps = Readonly<{
  plan: BoosterPlan;
}>;

/** The month-view study calendar in a card. Clicking an in-plan day opens the Daily Practice tab. */
export const StudyCalendarSection = ({ plan }: StudyCalendarSectionProps) => {
  const dispatch = useAppDispatch();
  const today = useMemo(() => toISODate(new Date()), []);
  const [visibleMonth, setVisibleMonth] = useState(() => monthOf(plan.startDate));

  const days = useMemo(() => plan.days.map(planDayToCalendarDay), [plan.days]);

  const handleDayClick = (date: string) => {
    dispatch(setBoosterSelectedDay(date));
    // openCustomTab both opens and selects the tab.
    dispatch(openCustomTab('dailyPractice'));
  };

  return (
    <Box sx={rootSx} id="booster-study-calendar">
      <MonthCalendar
        id="booster-month-calendar"
        startDate={plan.startDate}
        today={today}
        days={days}
        visibleMonth={visibleMonth}
        onMonthChange={setVisibleMonth}
        onDayClick={handleDayClick}
        headerAction={<ReplanControl />}
        emptyState={STRINGS.calendar.emptyState}
      />
    </Box>
  );
};

const rootSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  // A floor, not a frame — a tall month grows instead of clipping.
  minHeight: '912px',
  overflow: 'hidden',
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  // No semantic border token is this dark.
  border: `1px solid ${backrs.colors['Gray/500']}`,
  borderRadius: backrs.radius['radius-xl'],
});
